// src/pages/CandidateOnboarding.tsx: first-time candidate setup wizard
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import { C, uploadCV, parseCVWithAI, isCandidateComplete } from '../lib/talentConnect';
import { card, labelStyle, Banner } from '../lib/talentUI';
import { CandidateFields, CandidateFormState, emptyCandidateForm, candidatePayload } from '../lib/CandidateFields';

const STEPS = ['Upload CV', 'Review Profile', 'Visibility'];

export default function CandidateOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [step, setStep]         = useState(0);
  const [form, setForm]         = useState<CandidateFormState>({ ...emptyCandidateForm });
  const [cvPath, setCvPath]     = useState<string | null>(null);
  const [cvName, setCvName]     = useState<string | null>(null);
  const [visible, setVisible]   = useState(true);
  const [parsing, setParsing]   = useState(false);
  const [submitting, setSub]    = useState(false);
  const [error, setError]       = useState('');
  const [note, setNote]         = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase.from('candidates').select('id').eq('owner_id', user.id).maybeSingle();
      if (data) { navigate('/candidate-dashboard', { replace: true }); return; }
      setChecking(false);
    })();
  }, [user, navigate]);

  const handleCV = async (file: File) => {
    if (!user) return;
    if (file.type !== 'application/pdf') { setError('Please upload a PDF.'); return; }
    setError(''); setNote(''); setParsing(true); setCvName(file.name);
    try {
      // Upload + AI parse in parallel.
      const [path, parsed] = await Promise.all([uploadCV(user.id, file), parseCVWithAI(file)]);
      setCvPath(path);
      setForm(f => ({
        ...f,
        full_name: f.full_name || (user.user_metadata?.full_name as string) || '',
        current_role: parsed.current_role || f.current_role,
        current_employer: parsed.current_employer || f.current_employer,
        description: parsed.description || f.description,
        years_experience: parsed.years_experience ? String(parsed.years_experience) : f.years_experience,
        seniority: parsed.seniority || f.seniority,
        target_roles: parsed.target_roles.length ? parsed.target_roles : f.target_roles,
        asset_classes: parsed.asset_classes.length ? parsed.asset_classes : f.asset_classes,
        skills: parsed.skills.length ? parsed.skills : f.skills,
        relocation_countries: parsed.relocation_countries.length ? parsed.relocation_countries : f.relocation_countries,
      }));
      setNote('We pre-filled your profile from your CV. Review and edit everything on the next step.');
      setStep(1);
    } catch (e: any) {
      // Upload may still have succeeded even if parsing failed.
      setError(`Could not read your CV automatically (${e.message || 'AI unavailable'}). You can still fill in your profile manually.`);
      setStep(1);
    }
    setParsing(false);
  };

  const finish = async () => {
    if (!user) return;
    setSub(true); setError('');
    const payload = candidatePayload(form);
    const { error: insErr } = await supabase.from('candidates').insert({
      owner_id: user.id,
      ...payload,
      cv_url: cvPath,
      visible_on_platform: visible,
      profile_complete: isCandidateComplete(payload),
    });
    if (insErr) { setError(insErr.message); setSub(false); return; }
    navigate('/candidate-dashboard', { replace: true });
  };

  if (checking) return (
    <div style={{ display: 'flex', height: '70vh', alignItems: 'center', justifyContent: 'center', background: C.surface }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading…</div>
    </div>
  );

  return (
    <div style={{ padding: '40px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '26px', color: C.navy, fontWeight: 400, margin: '0 0 6px', letterSpacing: '-0.3px' }}>Welcome to TalentConnect</h1>
          <p style={{ fontSize: '13.5px', color: C.muted, margin: 0 }}>Set up your candidate profile so alternative investment firms can find you.</p>
        </div>

        {/* Progress */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            {STEPS.map((s, i) => (
              <div key={s} style={{ fontSize: '11px', fontWeight: i === step ? 700 : 600, color: i <= step ? C.navy : C.mutedLt, letterSpacing: '0.3px' }}>
                {i + 1}. {s}
              </div>
            ))}
          </div>
          <div style={{ height: '4px', background: C.borderLt, borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${((step + 1) / STEPS.length) * 100}%`, background: C.gold, transition: 'width 0.25s' }} />
          </div>
        </div>

        <div style={{ ...card, padding: '28px 30px' }}>
          {error && <Banner type="err" text={error} />}
          {note && !error && <Banner type="ok" text={note} />}

          {step === 0 && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '18px', color: C.navy, marginBottom: '8px' }}>Upload your CV</div>
              <p style={{ fontSize: '13px', color: C.muted, margin: '0 auto 22px', maxWidth: '420px', lineHeight: 1.6 }}>
                Upload a PDF and we'll use AI to pre-fill your profile, role, experience, target roles, asset classes and skills. You can edit everything afterwards.
              </p>
              <div onClick={() => !parsing && fileRef.current?.click()} style={{
                border: `2px dashed ${C.border}`, borderRadius: '6px', padding: '40px 20px', cursor: parsing ? 'default' : 'pointer',
                background: C.surface, maxWidth: '420px', margin: '0 auto',
              }}>
                <div style={{ fontSize: '30px', marginBottom: '10px' }}>📄</div>
                <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.text, marginBottom: '4px' }}>
                  {parsing ? 'Reading your CV…' : cvName || 'Click to choose a PDF'}
                </div>
                <div style={{ fontSize: '12px', color: C.muted }}>{parsing ? 'This can take a few seconds' : 'PDF up to ~10MB'}</div>
              </div>
              <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleCV(f); e.target.value = ''; }} />
              <div style={{ marginTop: '18px' }}>
                <button onClick={() => setStep(1)} disabled={parsing} style={{ background: 'none', border: 'none', color: C.muted, fontSize: '12.5px', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'system-ui' }}>
                  Skip and fill in manually
                </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '17px', color: C.navy, marginBottom: '18px' }}>Review & edit your profile</div>
              <CandidateFields form={form} setForm={setForm} />
            </>
          )}

          {step === 2 && (
            <>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '17px', color: C.navy, marginBottom: '16px' }}>Visibility</div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', padding: '16px', border: `1px solid ${C.border}`, borderRadius: '4px', marginBottom: '16px' }}>
                <input type="checkbox" checked={visible} onChange={e => setVisible(e.target.checked)} style={{ marginTop: '2px' }} />
                <div>
                  <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.text, marginBottom: '3px' }}>Visible on platform</div>
                  <div style={{ fontSize: '12.5px', color: C.muted, lineHeight: 1.6 }}>
                    When on, your profile appears in manager searches on TalentConnect. When off, you won't appear in searches, but the Altveno team can still flag you to specific roles they think you're a fit for.
                  </div>
                </div>
              </label>
              <div style={{ background: C.navy, borderRadius: '4px', padding: '16px 18px' }}>
                <div style={{ fontFamily: 'Georgia, serif', fontSize: '15px', color: C.white, marginBottom: '4px' }}>You're ready</div>
                <div style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>
                  Finish setup to publish your profile and start receiving connection requests from managers.
                </div>
              </div>
            </>
          )}

          {/* Nav */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '26px', paddingTop: '20px', borderTop: `1px solid ${C.borderLt}` }}>
            <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0 || submitting} style={{ ...(step === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}), background: C.white, color: C.textSec, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '10px 20px', fontSize: '12.5px', fontWeight: 600, cursor: step === 0 ? 'not-allowed' : 'pointer', fontFamily: 'system-ui' }}>Back</button>
            {step < STEPS.length - 1 ? (
              <button onClick={() => setStep(s => s + 1)} disabled={parsing} style={{ background: C.navy, color: C.white, border: 'none', borderRadius: '3px', padding: '10px 26px', fontSize: '12.5px', fontWeight: 600, cursor: parsing ? 'not-allowed' : 'pointer', fontFamily: 'system-ui', opacity: parsing ? 0.6 : 1 }}>Continue</button>
            ) : (
              <button onClick={finish} disabled={submitting} style={{ background: C.navy, color: C.white, border: 'none', borderRadius: '3px', padding: '10px 26px', fontSize: '12.5px', fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'system-ui', opacity: submitting ? 0.7 : 1 }}>{submitting ? 'Finishing…' : 'Finish & Go to Dashboard'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
